// Thin wrapper around Electron IPC for sync state coordination.
// Safe to import in non-Electron builds — every call no-ops.

export const isElectronHashBuild = () => {
  const isHashBuild = process.env.REACT_APP_BUILD_ROUTER?.toLowerCase() === "hash";
  if (!isHashBuild || typeof window === "undefined") return false;

  const isElectronRuntime =
    !!(window.process && window.process.type) ||
    (navigator && navigator.userAgent && navigator.userAgent.includes("Electron")) ||
    window.location.protocol === "file:";

  return isElectronRuntime;
};

export const isElectronEnvironment = () => isElectronHashBuild();

const getIpcRenderer = () => {
  if (!isElectronHashBuild()) return null;
  try {
    const electron =
      typeof window.require === "function" ? window.require("electron") : null;
    return electron && electron.ipcRenderer ? electron.ipcRenderer : null;
  } catch (err) {
    return null;
  }
};

const sendBestEffort = (channel, payload) => {
  const ipc = getIpcRenderer();
  if (!ipc) return;
  try {
    ipc.send(channel, payload);
  } catch (err) {
    // Non-fatal — Electron IPC is best-effort
  }
};

/**
 * Tell the Electron main process whether a sync is in progress.
 * Main uses this to confirm before letting the user close the window.
 */
export const reportSyncState = (isSyncing) => {
  sendBestEffort("sync-state", !!isSyncing);
};

/**
 * Tell the Electron main process whether initial-setup is in progress.
 * Main also uses this to confirm before letting the user close the window.
 */
export const reportInitialSetupState = (isSettingUp) => {
  sendBestEffort("initial-setup-state", !!isSettingUp);
};

/**
 * Check if initial setup has been completed for this app installation.
 * Checks localStorage first, and falls back to Electron IPC marker if present.
 */
export const isInitialSetupCompleted = () => {
  if (typeof localStorage !== "undefined") {
    const localFlag = localStorage.getItem("mediplus_initial_setup_completed");
    if (localFlag === "true") return true;
  }
  return false;
};

/**
 * Mark initial setup as completed persistently across restarts.
 */
export const markInitialSetupCompleted = () => {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("mediplus_initial_setup_completed", "true");
  }
  sendBestEffort("mark-initial-setup-completed", true);
};

/**
 * Reset initial setup marker so a fresh setup (and DB wipe) can be triggered.
 */
export const resetInitialSetup = () => {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("mediplus_initial_setup_completed");
  }
  sendBestEffort("reset-initial-setup", true);
};
